/**
 * Кэш данных для списка чатов.
 * Хранит профили и баттлы на уровне модуля, чтобы избежать N+1 запросов
 * при ререндере списка чатов.
 *
 * TTL: 5 минут — после этого данные считаются устаревшими
 * и перевыгружаются при следующем запросе.
 */

import { profilesApi } from '../../api/profiles';
import { battlesApi } from '../../api/battles';
import { presenceApi } from '../../api/presence';

const CACHE_TTL = 5 * 60 * 1000; // 5 минут

const profileCache = new Map();   // profileId → { data, fetchedAt }
const battleCache = new Map();    // battleId  → { data, fetchedAt }
const presenceCache = new Map();  // userId    → { data, fetchedAt }

/**
 * Получить профиль из кэша (если свежий) или null.
 */
function getCachedProfile(profileId) {
    const entry = profileCache.get(profileId);
    if (entry && Date.now() - entry.fetchedAt < CACHE_TTL) {
        return entry.data;
    }
    return null;
}

function getCachedBattle(battleId) {
    const entry = battleCache.get(battleId);
    if (entry && Date.now() - entry.fetchedAt < CACHE_TTL) {
        return entry.data;
    }
    return null;
}

function getCachedPresence(userId) {
    const entry = presenceCache.get(userId);
    if (entry && Date.now() - entry.fetchedAt < CACHE_TTL) {
        return entry.data;
    }
    return null;
}

/**
 * Нормализация профиля в формат, нужный ChatListItem.
 */
function normalizeProfile(profileData) {
    return {
        displayName: profileData.displayName || 'Пользователь',
        avatar: profileData.avatar ? `/api/files/${profileData.avatar}` : '/default-avatar.png',
        avatarId: profileData.avatar || null,
        userId: profileData.userId
    };
}

/**
 * Нормализация баттла в формат, нужный ChatListItem.
 */
function normalizeBattle(battleData, battleId) {
    const cover = battleData.coverUrl || battleData.cover;
    return {
        title: battleData.title || `Баттл #${battleId.slice(0, 8)}`,
        coverUrl: cover ? `/api/files/${cover}` : '/default-battle-avatar.png'
    };
}

/**
 * Пакетная загрузка данных для списка чатов.
 *
 * Собирает ID профилей (DIRECT-чаты) и ID баттлов (BATTLE_SUPPORT-чаты),
 * загружает их с кэшированием, батчит presence через getStatuses.
 *
 * @param {Array} chats — список чатов из API
 * @param {string} currentProfileId — ID профиля текущего пользователя
 * @returns {Promise<{ profiles: Map, battles: Map, presences: Map }>}
 */
export async function fetchChatListData(chats, currentProfileId) {
    const profileIdsToFetch = new Set();
    const battleIdsToFetch = new Set();

    for (const chat of chats) {
        if (chat.chatType === 'DIRECT') {
            const participants = chat.participantIds || chat.participants || [];
            const otherId = participants.find(pId => pId !== currentProfileId) || participants[0];
            if (otherId && !getCachedProfile(otherId)) {
                profileIdsToFetch.add(otherId);
            }
        } else if (chat.chatType === 'BATTLE_SUPPORT' && chat.battleId) {
            if (!getCachedBattle(chat.battleId)) {
                battleIdsToFetch.add(chat.battleId);
            }
        }
    }

    // Загружаем профили параллельно (с кэшированием)
    const profilePromises = Array.from(profileIdsToFetch).map(async (profileId) => {
        try {
            const response = await profilesApi.get(profileId);
            const data = response.data || response;
            const normalized = normalizeProfile(data);
            profileCache.set(profileId, { data: normalized, fetchedAt: Date.now() });
            return { profileId, data: normalized };
        } catch (err) {
            console.error('[chatDataCache] Failed to fetch profile:', profileId, err.message);
            const fallback = { displayName: 'Пользователь', avatar: '/default-avatar.png', userId: null };
            profileCache.set(profileId, { data: fallback, fetchedAt: Date.now() });
            return { profileId, data: fallback };
        }
    });

    // Загружаем баттлы параллельно (с кэшированием)
    const battlePromises = Array.from(battleIdsToFetch).map(async (battleId) => {
        try {
            const data = await battlesApi.get(battleId);
            const normalized = normalizeBattle(data, battleId);
            battleCache.set(battleId, { data: normalized, fetchedAt: Date.now() });
            return { battleId, data: normalized };
        } catch (err) {
            console.error('[chatDataCache] Failed to fetch battle:', battleId, err.message);
            const fallback = normalizeBattle({}, battleId);
            battleCache.set(battleId, { data: fallback, fetchedAt: Date.now() });
            return { battleId, data: fallback };
        }
    });

    await Promise.all([
        Promise.all(profilePromises),
        Promise.all(battlePromises)
    ]);

    // Собираем Map'ы с результатами (включая кэшированные)
    const profilesMap = new Map();
    for (const chat of chats) {
        if (chat.chatType === 'DIRECT') {
            const participants = chat.participantIds || chat.participants || [];
            const otherId = participants.find(pId => pId !== currentProfileId) || participants[0];
            if (otherId) {
                const cached = getCachedProfile(otherId);
                if (cached) profilesMap.set(otherId, cached);
            }
        }
    }

    const battlesMap = new Map();
    for (const chat of chats) {
        if (chat.chatType === 'BATTLE_SUPPORT' && chat.battleId) {
            const cached = getCachedBattle(chat.battleId);
            if (cached) battlesMap.set(chat.battleId, cached);
        }
    }

    // Батч presence для всех найденных userId
    const userIds = Array.from(profilesMap.values())
        .map(p => p.userId)
        .filter(Boolean);

    const userIdsToFetch = userIds.filter(uid => !getCachedPresence(uid));

    if (userIdsToFetch.length > 0) {
        try {
            const presences = await presenceApi.getStatuses(userIdsToFetch);
            if (Array.isArray(presences)) {
                for (const p of presences) {
                    presenceCache.set(p.userId, {
                        data: { isOnline: p.isOnline, lastActivity: p.lastActivity },
                        fetchedAt: Date.now()
                    });
                }
            }
        } catch (err) {
            console.error('[chatDataCache] Failed to batch fetch presences:', err.message);
        }
    }

    const presencesMap = new Map();
    for (const uid of userIds) {
        const cached = getCachedPresence(uid);
        if (cached) presencesMap.set(uid, cached.data);
    }

    return { profiles: profilesMap, battles: battlesMap, presences: presencesMap };
}

/**
 * Очистка кэша (например, при logout).
 */
export function clearChatDataCache() {
    profileCache.clear();
    battleCache.clear();
    presenceCache.clear();
}
